import { TIMING } from './taUtils';

const ROW_H = 30;

function Bar({ start, end, scale, className, testid, title }) {
    const left = scale(start);
    const width = Math.max(scale(end) - left, 0.6);
    return <div className={`absolute rounded-sm ${className}`} style={{ left: `${left}%`, width: `${width}%` }} title={title} data-testid={testid} />;
}

function Milestone({ at, scale, className, title }) {
    return <div className={`absolute w-2.5 h-2.5 rotate-45 -ml-[5px] ${className}`} style={{ left: `${scale(at)}%` }} title={title} />;
}

function GanttRow({ a, scale, planOnly }) {
    const t = TIMING[a.timing] || TIMING.sin_iniciar;
    const isMs = !a.duration_min;
    const hasActual = a.actual_start_min !== null && a.actual_start_min !== undefined;
    const actualEnd = a.actual_end_min ?? a.proj_end_min;
    const shifted = !planOnly && !hasActual && a.drag_min > 0;
    return (
        <div className="flex items-center border-b border-border/20 last:border-0" style={{ height: ROW_H }} data-testid={`gantt-row-${a.key}`}>
            <div className="w-48 sm:w-60 shrink-0 pr-2 truncate text-xs flex items-center gap-1.5">
                {a.critical && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" title="Ruta crítica" />}
                <span className={a.critical ? 'font-semibold' : 'text-muted-foreground'}>{a.name}</span>
            </div>
            <div className="relative flex-1 h-full">
                {isMs ? (
                    <Milestone at={a.base_start_min} scale={scale} className={`top-[4px] border ${a.critical ? 'border-red-400' : 'border-slate-400'} bg-transparent`} title="Planificado" />
                ) : (
                    <Bar start={a.base_start_min} end={a.base_end_min} scale={scale} className={`top-[4px] h-2 border ${a.critical ? 'border-red-400/80 bg-red-500/10' : 'border-slate-400/50 bg-slate-500/10'}`} testid={`gantt-plan-${a.key}`} title={`Planificado ${a.base_start_min}→${a.base_end_min} min`} />
                )}
                {shifted && !isMs && (
                    <Bar start={a.proj_start_min} end={a.proj_end_min} scale={scale} className="top-[4px] h-2 border border-dashed border-amber-400 bg-amber-500/10" testid={`gantt-shift-${a.key}`} title={`Desplazada +${a.drag_min} min`} />
                )}
                {shifted && (
                    <span className="absolute top-[1px] text-[9px] font-mono text-amber-700 dark:text-amber-400 whitespace-nowrap" style={{ left: `calc(${scale(a.proj_end_min)}% + 4px)` }} data-testid={`gantt-drag-${a.key}`}>+{a.drag_min}</span>
                )}
                {!planOnly && hasActual && (isMs ? (
                    <Milestone at={a.actual_start_min} scale={scale} className={`top-[15px] ${t.bar}`} title={t.label} />
                ) : (
                    <Bar start={a.actual_start_min} end={actualEnd} scale={scale} className={`top-[15px] h-2.5 ${t.bar} ${a.state === 'en_curso' ? 'animate-pulse' : ''}`} testid={`gantt-actual-${a.key}`} title={`Real · ${t.label}`} />
                ))}
            </div>
        </div>
    );
}

export function FlightGantt({ activities, standard, nowMin, showPre = true, planOnly = false }) {
    const rows = activities.filter(a => showPre || a.offset_min >= 0);
    if (!rows.length) return <p className="text-sm text-muted-foreground">Sin actividades</p>;
    const vals = rows.flatMap(a => [a.base_start_min, a.base_end_min, ...(planOnly ? [] : [a.proj_start_min, a.proj_end_min, a.actual_start_min, a.actual_end_min])]).filter(v => v !== null && v !== undefined);
    const lo = Math.floor(Math.min(0, ...vals) / 5) * 5;
    let hi = Math.max(standard || 0, ...vals);
    if (!planOnly && nowMin !== null && nowMin !== undefined && nowMin <= hi + 30) hi = Math.max(hi, nowMin);
    hi = Math.ceil((hi + 2) / 5) * 5;
    const scale = (m) => ((Math.min(Math.max(m, lo), hi) - lo) / (hi - lo)) * 100;
    const step = hi - lo > 90 ? 15 : hi - lo > 45 ? 10 : 5;
    const ticks = [];
    for (let m = Math.ceil(lo / step) * step; m <= hi; m += step) ticks.push(m);
    return (
        <div className="overflow-x-auto" data-testid="flight-gantt">
            <div className="min-w-[640px]">
                <div className="flex text-[10px] font-mono text-muted-foreground mb-1">
                    <div className="w-48 sm:w-60 shrink-0">min desde calzos</div>
                    <div className="relative flex-1 h-4">
                        {ticks.map((m, i) => <span key={m} className={`absolute ${i === ticks.length - 1 && scale(m) > 97 ? '-translate-x-full' : '-translate-x-1/2'}`} style={{ left: `${scale(m)}%` }}>{m}</span>)}
                    </div>
                </div>
                <div className="relative">
                    <div className="absolute inset-0 flex pointer-events-none">
                        <div className="w-48 sm:w-60 shrink-0" />
                        <div className="relative flex-1">
                            {ticks.map(m => <div key={m} className="absolute top-0 bottom-0 border-l border-border/20" style={{ left: `${scale(m)}%` }} />)}
                            <div className="absolute top-0 bottom-0 border-l-2 border-sky-400/70" style={{ left: `${scale(0)}%` }} title="Calzos (min 0)" />
                            {standard > 0 && <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-400/80" style={{ left: `${scale(standard)}%` }} data-testid="gantt-standard-line" title={`Estándar ${standard} min`} />}
                            {!planOnly && nowMin !== null && nowMin !== undefined && nowMin >= lo && nowMin <= hi && (
                                <div className="absolute top-0 bottom-0 border-l-2 border-primary" style={{ left: `${scale(nowMin)}%` }} data-testid="gantt-now-line" title="Ahora" />
                            )}
                        </div>
                    </div>
                    {rows.map(a => <GanttRow key={a.key} a={a} scale={scale} planOnly={planOnly} />)}
                </div>
                <GanttLegend planOnly={planOnly} standard={standard} />
            </div>
        </div>
    );
}

function GanttLegend({ planOnly, standard }) {
    const item = (cls, label) => <span className="flex items-center gap-1"><span className={`inline-block w-4 h-2 rounded-sm ${cls}`} />{label}</span>;
    return (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[10px] text-muted-foreground" data-testid="gantt-legend">
            {item('border border-slate-400/50 bg-slate-500/10', 'Planificado')}
            {item('border border-red-400/80 bg-red-500/10', 'Ruta crítica')}
            {!planOnly && item('border border-dashed border-amber-400 bg-amber-500/10', 'Desplazada (+min que arrastra)')}
            {!planOnly && item(TIMING.a_tiempo.bar, 'A tiempo')}
            {!planOnly && item(TIMING.cerca.bar, 'Cerca del límite')}
            {!planOnly && item(TIMING.excedido.bar, 'Excedido')}
            <span className="flex items-center gap-1"><span className="inline-block h-3 border-l-2 border-dashed border-red-400" />Estándar {standard} min</span>
        </div>
    );
}
